/**
 * Deterministic auto-mapping from an arbitrary source skeleton onto the canonical humanoid rig.
 *
 * No machine learning, no randomness, no I/O: the same skeleton always produces the same mapping.
 * Signals, in the order they are trusted:
 *   1. normalized bone names (prefixes such as `mixamorig:` / `Armature|` / `DEF-` stripped),
 *   2. left/right tokens in the name (`Left…`, `L_`, `.R`, `_lft`, …),
 *   3. hierarchy (spine chain ordering, limb bones below their limb root),
 *   4. rest-pose geometry (x sign after the world-correction guess) when names carry no side.
 */

import { Matrix4, Quaternion, Vector3 } from 'three';

import { CANONICAL_BONE_IDS, identityQuat } from '../shared';
import type { CanonicalBoneId, QuaternionTuple } from '../shared';
import { isDescendantOf, type SourceBoneInfo } from './sourceSkeleton';

export type MappingConfidence = 'confident' | 'uncertain' | 'missing';

export interface AutoMapResult {
  canonicalToSourceBone: Partial<Record<CanonicalBoneId, string>>;
  confidence: Record<CanonicalBoneId, MappingConfidence>;
  notes: string[];
}

type Side = 'left' | 'right';

type Role =
  | 'Root'
  | 'Hips'
  | 'SpineChain'
  | 'Neck'
  | 'Head'
  | 'Shoulder'
  | 'UpperArm'
  | 'LowerArm'
  | 'Hand'
  | 'UpperLeg'
  | 'LowerLeg'
  | 'Foot'
  | 'Toe'
  | 'Ignore';

// ---------------------------------------------------------------------------
// Name normalization
// ---------------------------------------------------------------------------

/** Tokens that are pure naming noise and never carry meaning. */
const PREFIX_TOKENS: ReadonlySet<string> = new Set([
  'mixamorig',
  'mixamorig1',
  'mixamorig2',
  'armature',
  'skeleton',
  'rig',
  'def',
  'org',
  'mch',
  'ctrl',
  'ctl',
  'bip',
  'bip01',
  'bip001',
  'biped',
  'bone',
  'joint',
  'jnt',
  'character1',
]);

/** Glued prefixes to peel off once the tokens have been joined (sanitized names lose separators). */
const GLUED_PREFIXES: readonly string[] = [
  'mixamorig1',
  'mixamorig2',
  'mixamorig',
  'character1',
  'armature',
  'skeleton',
  'bip001',
  'bip01',
  'biped',
  'def',
  'org',
  'mch',
];

const LEFT_TOKENS: ReadonlySet<string> = new Set(['l', 'lf', 'lft', 'left']);
const RIGHT_TOKENS: ReadonlySet<string> = new Set(['r', 'rt', 'rgt', 'right']);

function tokenize(raw: string): string[] {
  // `Armature|Hips` / `rig/Hips` — keep only the last path segment.
  const lastPipe = Math.max(raw.lastIndexOf('|'), raw.lastIndexOf('/'));
  const segment = lastPipe >= 0 ? raw.slice(lastPipe + 1) : raw;
  return segment
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 0);
}

/** `left` / `right` when the raw name says so, otherwise `null`. */
export function detectSideFromName(raw: string): Side | null {
  const tokens = tokenize(raw);
  for (const token of tokens) {
    if (LEFT_TOKENS.has(token)) return 'left';
    if (RIGHT_TOKENS.has(token)) return 'right';
  }
  const compact = tokens.join('');
  if (compact.includes('left')) return 'left';
  if (compact.includes('right')) return 'right';
  return null;
}

/**
 * Reduce a raw bone name to its semantic core: `mixamorig:LeftForeArm` → `forearm`,
 * `DEF-upper_arm.L` → `upperarm`, `Armature|Spine2` → `spine2`.
 */
export function normalizeBoneName(raw: string): string {
  const tokens = tokenize(raw);
  const kept = tokens.filter(
    (t) => !PREFIX_TOKENS.has(t) && !LEFT_TOKENS.has(t) && !RIGHT_TOKENS.has(t),
  );
  const meaningful = kept.filter((t) => !/^[0-9]+$/.test(t));
  let core = (meaningful.length > 0 ? meaningful : kept).join('');

  let peeled = true;
  while (peeled) {
    peeled = false;
    for (const prefix of GLUED_PREFIXES) {
      if (core.length > prefix.length && core.startsWith(prefix)) {
        core = core.slice(prefix.length);
        peeled = true;
        break;
      }
    }
  }

  for (const side of ['left', 'right'] as const) {
    if (core.length > side.length && core.startsWith(side)) core = core.slice(side.length);
    if (core.length > side.length && core.endsWith(side)) core = core.slice(0, -side.length);
  }

  return core;
}

// ---------------------------------------------------------------------------
// Role classification
// ---------------------------------------------------------------------------

/** Checked in order; the first substring hit wins. Longer/more specific names come first. */
const KEYWORDS: readonly (readonly [string, Role])[] = [
  ['toebase', 'Toe'],
  ['toe', 'Toe'],
  ['forearm', 'LowerArm'],
  ['lowerarm', 'LowerArm'],
  ['armlower', 'LowerArm'],
  ['elbow', 'LowerArm'],
  ['upperarm', 'UpperArm'],
  ['armupper', 'UpperArm'],
  ['uparm', 'UpperArm'],
  ['shoulder', 'Shoulder'],
  ['clavicle', 'Shoulder'],
  ['collar', 'Shoulder'],
  ['hand', 'Hand'],
  ['wrist', 'Hand'],
  ['upperleg', 'UpperLeg'],
  ['legupper', 'UpperLeg'],
  ['upleg', 'UpperLeg'],
  ['thigh', 'UpperLeg'],
  ['lowerleg', 'LowerLeg'],
  ['leglower', 'LowerLeg'],
  ['calf', 'LowerLeg'],
  ['shin', 'LowerLeg'],
  ['knee', 'LowerLeg'],
  ['foot', 'Foot'],
  ['ankle', 'Foot'],
  ['head', 'Head'],
  ['neck', 'Neck'],
  ['upperchest', 'SpineChain'],
  ['chest', 'SpineChain'],
  ['spine', 'SpineChain'],
  ['ribcage', 'SpineChain'],
  ['torso', 'SpineChain'],
  ['abdomen', 'SpineChain'],
  ['waist', 'SpineChain'],
  ['pelvis', 'Hips'],
  ['hips', 'Hips'],
  ['hip', 'Hips'],
  ['arm', 'UpperArm'],
  ['leg', 'LowerLeg'],
  ['root', 'Root'],
];

/** Cores that name a role outright (score 100) rather than merely containing a keyword (70). */
const EXACT_ROLES: ReadonlyMap<string, Role> = new Map<string, Role>([
  ...KEYWORDS.map(([k, r]) => [k, r] as [string, Role]),
  ['spine1', 'SpineChain'],
  ['spine2', 'SpineChain'],
  ['spine3', 'SpineChain'],
  ['spine01', 'SpineChain'],
  ['spine02', 'SpineChain'],
  ['spine03', 'SpineChain'],
  ['chest1', 'SpineChain'],
  ['neck1', 'Neck'],
  ['head1', 'Head'],
]);

/** Auxiliary bones that must never be mapped: twist / roll / helper / IK rigging extras. */
const NEVER_MAP = /twist|roll|helper|target|pole|corrective|tweak|scale|socket|attach/;

function classifyCore(core: string): { role: Role; score: number } {
  if (core.length === 0) return { role: 'Ignore', score: 0 };
  if (NEVER_MAP.test(core)) return { role: 'Ignore', score: 0 };

  const exact = EXACT_ROLES.get(core);
  if (exact) return { role: exact, score: 100 };

  for (const [keyword, role] of KEYWORDS) {
    if (core.includes(keyword)) return { role, score: 70 };
  }
  return { role: 'Ignore', score: 0 };
}

const SIDED_ROLES: ReadonlySet<Role> = new Set<Role>([
  'Shoulder',
  'UpperArm',
  'LowerArm',
  'Hand',
  'UpperLeg',
  'LowerLeg',
  'Foot',
  'Toe',
]);

interface Candidate {
  info: SourceBoneInfo;
  role: Role;
  side: Side | null;
  score: number;
  sideFromName: boolean;
}

// ---------------------------------------------------------------------------
// World correction (plan D15)
// ---------------------------------------------------------------------------

function snapAxis(v: Vector3, out: Vector3): Vector3 {
  const ax = Math.abs(v.x);
  const ay = Math.abs(v.y);
  const az = Math.abs(v.z);
  if (ax >= ay && ax >= az) return out.set(v.x < 0 ? -1 : 1, 0, 0);
  if (ay >= az) return out.set(0, v.y < 0 ? -1 : 1, 0);
  return out.set(0, 0, v.z < 0 ? -1 : 1);
}

/** Round a rotation to the nearest multiple of 90° on every axis; identity when degenerate. */
function snapToRightAngles(q: Quaternion): Quaternion {
  const m = new Matrix4().makeRotationFromQuaternion(q);
  const x = new Vector3();
  const y = new Vector3();
  const z = new Vector3();
  m.extractBasis(x, y, z);
  snapAxis(x, x);
  snapAxis(y, y);
  snapAxis(z, z);
  const snapped = new Matrix4().makeBasis(x, y, z);
  if (Math.abs(snapped.determinant() - 1) > 1e-6) return new Quaternion();
  return new Quaternion().setFromRotationMatrix(snapped);
}

/**
 * Guess the rotation that takes the source model's world frame to the canonical one
 * (Y up, character-left = +X, facing +Z). Uses hips→head as the up axis and
 * LeftHand−RightHand as the character-left axis; the result is snapped to whole 90° steps, so a
 * model that is already canonical yields exactly identity.
 */
export function guessWorldCorrection(
  bones: readonly SourceBoneInfo[],
  mapping: Partial<Record<CanonicalBoneId, string>>,
): QuaternionTuple {
  const byName = new Map<string, SourceBoneInfo>();
  for (const b of bones) if (!byName.has(b.name)) byName.set(b.name, b);

  const pos = (id: CanonicalBoneId): Vector3 | null => {
    const name = mapping[id];
    if (!name) return null;
    const info = byName.get(name);
    if (!info) return null;
    const p = info.restWorldPosition;
    return new Vector3(p[0], p[1], p[2]);
  };

  const hips = pos('Hips');
  const head = pos('Head');
  if (!hips || !head) return identityQuat();

  const up = new Vector3().subVectors(head, hips);
  if (up.lengthSq() < 1e-12) return identityQuat();
  up.normalize();

  const leftHand = pos('LeftHand');
  const rightHand = pos('RightHand');

  let rotation: Quaternion;
  if (leftHand && rightHand) {
    // Character-left in source space.
    const left = new Vector3().subVectors(leftHand, rightHand);
    left.addScaledVector(up, -left.dot(up));
    if (left.lengthSq() < 1e-12) {
      rotation = new Quaternion().setFromUnitVectors(up, new Vector3(0, 1, 0));
    } else {
      left.normalize();
      const forward = new Vector3().crossVectors(left, up);
      // Columns are the source-space images of canonical +X, +Y, +Z — i.e. the inverse rotation.
      const inverse = new Matrix4().makeBasis(left, up, forward);
      rotation = new Quaternion().setFromRotationMatrix(inverse).invert();
    }
  } else {
    rotation = new Quaternion().setFromUnitVectors(up, new Vector3(0, 1, 0));
  }

  const snapped = snapToRightAngles(rotation);
  return [snapped.x, snapped.y, snapped.z, snapped.w];
}

// ---------------------------------------------------------------------------
// Auto mapping
// ---------------------------------------------------------------------------

function bestFirst(a: Candidate, b: Candidate): number {
  if (a.score !== b.score) return b.score - a.score;
  if (a.info.depth !== b.info.depth) return a.info.depth - b.info.depth;
  return a.info.name < b.info.name ? -1 : a.info.name > b.info.name ? 1 : 0;
}

/**
 * Propose a canonical→source bone mapping. Never maps the same source bone twice; when two
 * canonical bones want the same source bone the higher-scoring one keeps it and the other is
 * reported as `missing` with a note.
 */
export function autoMapSkeleton(bones: readonly SourceBoneInfo[]): AutoMapResult {
  const notes: string[] = [];
  const canonicalToSourceBone: Partial<Record<CanonicalBoneId, string>> = {};
  const confidence = {} as Record<CanonicalBoneId, MappingConfidence>;
  for (const id of CANONICAL_BONE_IDS) confidence[id] = 'missing';

  const byName = new Map<string, SourceBoneInfo>();
  for (const b of bones) if (!byName.has(b.name)) byName.set(b.name, b);

  const candidates: Candidate[] = [];
  for (const info of bones) {
    const core = normalizeBoneName(info.name);
    const { role, score } = classifyCore(core);
    if (role === 'Ignore' || score === 0) continue;
    candidates.push({
      info,
      role,
      side: detectSideFromName(info.name),
      score,
      sideFromName: detectSideFromName(info.name) !== null,
    });
  }

  const byRole = (role: Role): Candidate[] =>
    candidates.filter((c) => c.role === role).sort(bestFirst);

  // --- pass 1: side-independent bones, so the world correction can be guessed ----------------
  const preliminary: Partial<Record<CanonicalBoneId, string>> = {};
  const firstHips = byRole('Hips')[0] ?? byRole('Root')[0];
  const firstHead = byRole('Head')[0];
  if (firstHips) preliminary.Hips = firstHips.info.name;
  if (firstHead) preliminary.Head = firstHead.info.name;
  for (const c of candidates) {
    if (c.role !== 'Hand' || !c.sideFromName) continue;
    const key: CanonicalBoneId = c.side === 'left' ? 'LeftHand' : 'RightHand';
    if (!preliminary[key]) preliminary[key] = c.info.name;
  }

  const worldCorrection = guessWorldCorrection(bones, preliminary);
  const correction = new Quaternion(
    worldCorrection[0],
    worldCorrection[1],
    worldCorrection[2],
    worldCorrection[3],
  );

  // --- pass 2: sides that the names did not provide, from rest-pose geometry ------------------
  const scratch = new Vector3();
  let geometrySides = 0;
  for (const c of candidates) {
    if (c.side !== null || !SIDED_ROLES.has(c.role)) continue;
    const p = c.info.restWorldPosition;
    scratch.set(p[0], p[1], p[2]).applyQuaternion(correction);
    if (Math.abs(scratch.x) < 1e-4) continue;
    c.side = scratch.x > 0 ? 'left' : 'right';
    geometrySides++;
  }
  if (geometrySides > 0) {
    notes.push(
      `${geometrySides} bone(s) had no left/right token in their name; the side was taken from ` +
        `the rest-pose x position instead.`,
    );
  }

  // --- assignment ------------------------------------------------------------------------------
  const used = new Set<string>();

  const assign = (
    id: CanonicalBoneId,
    pool: Candidate[],
    conf: MappingConfidence,
    ancestorName?: string,
  ): Candidate | null => {
    const ranked = pool
      .slice()
      .sort((a, b) => {
        if (ancestorName) {
          const da = isDescendantOf(byName, a.info.name, ancestorName) ? 1 : 0;
          const db = isDescendantOf(byName, b.info.name, ancestorName) ? 1 : 0;
          if (da !== db) return db - da;
        }
        return bestFirst(a, b);
      })
      .filter((c) => !used.has(c.info.name));
    const chosen = ranked[0];
    if (!chosen) return null;
    used.add(chosen.info.name);
    canonicalToSourceBone[id] = chosen.info.name;
    confidence[id] = conf === 'confident' && chosen.score < 100 ? 'uncertain' : conf;
    return chosen;
  };

  // Hips — a lone `Root`-named bone stands in when there is no hips/pelvis bone.
  const hipsPool = byRole('Hips');
  if (hipsPool.length > 0) {
    assign('Hips', hipsPool, 'confident');
  } else {
    const rootPool = byRole('Root');
    if (rootPool.length > 0) {
      assign('Hips', rootPool, 'uncertain');
      notes.push('No hips/pelvis bone found; the root bone is used as Hips.');
    } else {
      notes.push('No hips/pelvis bone found.');
    }
  }

  // Spine chain — lowest becomes Spine, topmost becomes Chest (plan D16).
  const spineChain = byRole('SpineChain').sort((a, b) => a.info.depth - b.info.depth);
  if (spineChain.length === 0) {
    notes.push('No spine bone found; Spine and Chest are unmapped.');
  } else if (spineChain.length === 1) {
    assign('Spine', spineChain, 'confident');
    notes.push(
      'Only one spine bone exists, so Chest is left unmapped — mapping it to the same bone ' +
        'would duplicate a source bone. Map Chest manually if the rig has a chest bone under ' +
        'another name.',
    );
  } else {
    const first = spineChain[0];
    const last = spineChain[spineChain.length - 1];
    assign('Spine', [first], 'confident');
    assign('Chest', [last], 'confident');
  }

  assign('Neck', byRole('Neck'), 'confident');
  assign('Head', byRole('Head'), 'confident');

  const sided = (role: Role, side: Side): Candidate[] =>
    candidates.filter((c) => c.role === role && c.side === side).sort(bestFirst);

  const limbs: { side: Side; upper: CanonicalBoneId; lower: CanonicalBoneId; end: CanonicalBoneId }[] =
    [
      { side: 'left', upper: 'LeftUpperArm', lower: 'LeftLowerArm', end: 'LeftHand' },
      { side: 'right', upper: 'RightUpperArm', lower: 'RightLowerArm', end: 'RightHand' },
    ];

  for (const limb of limbs) {
    let upper = assign(limb.upper, sided('UpperArm', limb.side), 'confident');
    if (!upper) {
      // A shoulder/clavicle bone only stands in for the upper arm when no arm bone exists.
      upper = assign(limb.upper, sided('Shoulder', limb.side), 'uncertain');
      if (upper) {
        notes.push(
          `${limb.upper}: no arm bone found on the ${limb.side} side; the shoulder/clavicle ` +
            `bone "${upper.info.name}" was used instead.`,
        );
      }
    }
    const lower = assign(limb.lower, sided('LowerArm', limb.side), 'confident', upper?.info.name);
    assign(limb.end, sided('Hand', limb.side), 'confident', lower?.info.name ?? upper?.info.name);
  }

  const legs: { side: Side; upper: CanonicalBoneId; lower: CanonicalBoneId; end: CanonicalBoneId }[] =
    [
      { side: 'left', upper: 'LeftUpperLeg', lower: 'LeftLowerLeg', end: 'LeftFoot' },
      { side: 'right', upper: 'RightUpperLeg', lower: 'RightLowerLeg', end: 'RightFoot' },
    ];

  for (const leg of legs) {
    const upper = assign(leg.upper, sided('UpperLeg', leg.side), 'confident');
    const lower = assign(leg.lower, sided('LowerLeg', leg.side), 'confident', upper?.info.name);
    assign(leg.end, sided('Foot', leg.side), 'confident', lower?.info.name ?? upper?.info.name);
  }

  // Root last: it is optional and must never steal a bone another canonical bone needs.
  assign('Root', byRole('Root'), 'confident');

  // Bones whose side had to be guessed are never reported as fully confident.
  for (const c of candidates) {
    if (c.sideFromName || c.side === null) continue;
    for (const id of CANONICAL_BONE_IDS) {
      if (canonicalToSourceBone[id] === c.info.name && confidence[id] === 'confident') {
        confidence[id] = 'uncertain';
      }
    }
  }

  for (const id of CANONICAL_BONE_IDS) {
    if (!canonicalToSourceBone[id]) confidence[id] = 'missing';
  }

  return { canonicalToSourceBone, confidence, notes };
}
